import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CompanyRegistrationInfo } from '../services/api';

interface AdminCompanyRegistrationProps {
  registration: CompanyRegistrationInfo | null | undefined;
}

/**
 * Who registered the contractor (story S3). Read-only and only used on admin
 * pages: the link is set at registration and cannot be changed, and it is
 * never shown to contractors or other users.
 */
export function AdminCompanyRegistration({ registration }: AdminCompanyRegistrationProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en-GB' : 'sv-SE';
  const date = registration?.registeredAt
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(registration.registeredAt))
    : '';

  let text: string;
  if (registration?.source === 'etappio_staff') {
    const name = registration.registeredByName || registration.registeredByEmail || t('adminCompanyRegistrations.noName');
    const role = registration.registrarRole
      ? t(`adminCompanyRegistrations.registrarRole.${registration.registrarRole}`)
      : t('adminCompanyView.registration.unknownRole');
    text = date
      ? t('adminCompanyView.registration.byStaff', { name, role, date })
      : t('adminCompanyView.registration.byStaffNoDate', { name, role });
  } else if (registration?.source === 'self_registered') {
    text = date
      ? t('adminCompanyView.registration.selfRegistered', { date })
      : t('adminCompanyView.registration.selfRegisteredNoDate');
  } else {
    text = t('adminCompanyView.registration.missing');
  }

  return (
    <section data-testid="admin-company-registration" aria-labelledby="admin-company-registration-title">
      <h6 id="admin-company-registration-title" className="fw-bold mb-2">{t('adminCompanyView.registration.title')}</h6>
      <p className="mb-1 text-break">
        {text}
        {registration?.derived && (
          <span className="badge bg-soft-warning text-warning ms-2">{t('adminCompanyView.registration.derived')}</span>
        )}
      </p>
      {registration?.derived && (
        <p className="small text-muted mb-1">{t('adminCompanyView.registration.derivedHint')}</p>
      )}
      <p className="small text-muted mb-0">{t('adminCompanyView.registration.readOnly')}</p>
    </section>
  );
}
